import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, CopyIcon, CheckIcon, ExternalLinkIcon, ClockIcon } from 'lucide-react';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { getEntryById, getRelatedEntries, getPayoutForEntry, type BalanceEntry, type BalanceTypeLabel, type ActivityType } from '../mocks/balanceData';
const balanceTypeColors: Record<BalanceTypeLabel, string> = {
  Available: 'bg-green-500',
  Incoming: 'bg-amber-400',
  Reserved: 'bg-yellow-400',
  'On hold': 'bg-red-400'
};
const balanceTypeBadge: Record<BalanceTypeLabel, string> = {
  Available: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Incoming: 'bg-amber-50 text-amber-700 border-amber-200',
  Reserved: 'bg-yellow-50 text-yellow-800 border-yellow-200',
  'On hold': 'bg-red-50 text-red-700 border-red-200'
};
function formatDateTimeLong(d: Date) {
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}
function CopyableId({
  label,
  value,
  mono = true
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* noop */
    }
  };
  return <div className="flex items-start justify-between gap-3 py-3 border-b border-gray-100 last:border-0" data-id="element-2792">
      <div className="min-w-0" data-id="element-2793">
        <div className="text-xs text-gray-500 mb-0.5" data-id="element-2794">{label}</div>
        <div className={`text-sm text-gray-900 break-all ${mono ? 'font-mono' : ''}`} data-id="element-2795">
          {value ?? <span className="text-gray-300" data-id="element-2796">—</span>}
        </div>
      </div>
      {value && <button type="button" onClick={handleCopy} className="flex-shrink-0 p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors" aria-label={`Copy ${label}`} data-id="element-2797">
          {copied ? <CheckIcon size={14} className="text-emerald-600" data-id="element-2798" /> : <CopyIcon size={14} data-id="element-2799" />}
        </button>}
    </div>;
}
function activityDot(activityType: ActivityType, balanceType: BalanceTypeLabel) {
  if (activityType === 'Chargeback' || activityType === 'Chargeback fee') return 'bg-red-500';
  if (activityType === 'Refund' || activityType === 'Void') return 'bg-orange-400';
  if (activityType === 'Payout') return 'bg-gray-700';
  if (activityType === 'Review hold') return 'bg-red-400';
  if (activityType === 'Review release' || activityType === 'Chargeback reversal') return 'bg-emerald-500';
  return balanceTypeColors[balanceType];
}
export function BalanceActivityDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const entry = id ? getEntryById(id) : undefined;
  if (!entry) {
    return <div className="max-w-4xl mx-auto py-16 text-center" data-id="element-2800">
        <p className="text-gray-600 mb-4" data-id="element-2801">Balance activity not found.</p>
        <Link to="/balance-activity" className="text-sm font-medium text-yellow-700 hover:text-yellow-800" data-id="element-2802">
          ← Back to Balance activity
        </Link>
      </div>;
  }
  const related = getRelatedEntries(entry.reference);
  const payoutEntry = getPayoutForEntry(entry);
  const isCredit = entry.amount >= 0;
  const formatAmt = (amt: number) => formatCurrencyAmount(Math.abs(amt), {
    fractionDigits: 2
  });
  // Derive a charge ID for entries that came from a customer payment.
  const chargeId = entry.reference.startsWith('ch_') ? entry.reference : null;
  const payoutId = entry.activityType === 'Payout' ? entry.reference : payoutEntry?.reference ?? null;
  const feeId = entry.reference.startsWith('fee_') ? entry.reference : null;
  return <div className="max-w-6xl mx-auto" data-id="element-2803">
      {/* Back link */}
      <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors mb-6" data-id="element-2804">
        <ArrowLeftIcon size={14} data-id="element-2805" />
        Balance activity
      </button>

      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6" data-id="element-2806">
        <div className="flex items-start justify-between gap-4 flex-wrap" data-id="element-2807">
          <div data-id="element-2808">
            <div className="flex items-center gap-2 mb-3" data-id="element-2809">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border rounded-full ${balanceTypeBadge[entry.balanceType]}`} data-id="element-2810">
                <span className={`w-1.5 h-1.5 rounded-full ${balanceTypeColors[entry.balanceType]}`} data-id="element-2811" />
                {entry.balanceType}
              </span>
              <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-full" data-id="element-2812">
                {entry.activityType}
              </span>
              <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-full" data-id="element-2813">
                {entry.status}
              </span>
            </div>
            <div className="flex items-baseline gap-3" data-id="element-2814">
              <span className={`text-4xl font-semibold tracking-tight ${isCredit ? 'text-emerald-600' : 'text-gray-900'}`} data-id="element-2815">
                {isCredit ? '+' : '−'}
                {formatAmt(entry.amount)}
              </span>
              <span className="text-sm text-gray-500" data-id="element-2816">{entry.description}</span>
            </div>
            <div className="flex items-center gap-1.5 mt-3 text-sm text-gray-500" data-id="element-2817">
              <ClockIcon size={13} data-id="element-2818" />
              {formatDateTimeLong(entry.date)}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2819">
        {/* Left: Summary + Timeline */}
        <div className="lg:col-span-2 space-y-6" data-id="element-2820">
          {/* Summary */}
          <div className="bg-white border border-gray-200 rounded-lg" data-id="element-2821">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-2822">
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-2823">Summary</h2>
            </div>
            <dl className="divide-y divide-gray-100" data-id="element-2824">
              <SummaryRow label="Effective date" value={formatDateTimeLong(entry.date)} data-id="element-2825" />
              <SummaryRow label="Available on" value={entry.availableOn ? formatDateTimeLong(entry.availableOn) : entry.balanceType === 'On hold' ? 'Under review' : '—'} data-id="element-2826" />
              <SummaryRow label="Payout date" value={entry.payoutDate ? formatDateTimeLong(entry.payoutDate) : '—'} data-id="element-2827" />
              <SummaryRow label="Amount" value={`${isCredit ? '+' : '−'}${formatAmt(entry.amount)}`} valueClass={isCredit ? 'text-emerald-600' : 'text-gray-900'} data-id="element-2828" />
              <SummaryRow label="Balance bucket" value={entry.balanceType} data-id="element-2829" />
              <SummaryRow label="Activity type" value={entry.activityType} data-id="element-2830" />
              <SummaryRow label="Status" value={entry.status} data-id="element-2831" />
            </dl>
          </div>

          {/* Related events / timeline */}
          {related.length > 1 && <div className="bg-white border border-gray-200 rounded-lg" data-id="element-2832">
              <div className="px-6 py-4 border-b border-gray-200" data-id="element-2833">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-2834">
                  Related activity
                </h2>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-2835">
                  All ledger entries generated by source{' '}
                  <span className="font-mono" data-id="element-2836">{entry.reference}</span>
                </p>
              </div>
              <ol className="relative px-6 py-4" data-id="element-2837">
                <span className="absolute left-9 top-6 bottom-6 w-px bg-gray-200" aria-hidden data-id="element-2838" />
                {related.map(r => {
              const isCurrent = r.id === entry.id;
              const credit = r.amount >= 0;
              return <li key={r.id} className="relative pl-9 py-3" data-id="element-2839">
                      <span className={`absolute left-2 top-4 w-3 h-3 rounded-full ring-4 ring-white ${activityDot(r.activityType, r.balanceType)}`} data-id="element-2840" />
                      <div className="flex items-start justify-between gap-4" data-id="element-2841">
                        <div className="min-w-0" data-id="element-2842">
                          <div className="flex items-center gap-2 flex-wrap" data-id="element-2843">
                            <Link to={`/balance-activity/${r.id}`} className={`text-sm font-medium ${isCurrent ? 'text-gray-900' : 'text-gray-700 hover:text-yellow-700'}`} data-id="element-2844">
                              {r.activityType}
                            </Link>
                            <span className="text-xs text-gray-500" data-id="element-2845">
                              {r.balanceType}
                            </span>
                            {isCurrent && <span className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 px-1.5 py-0.5 rounded" data-id="element-2846">
                                This entry
                              </span>}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5" data-id="element-2847">
                            {formatDateTimeLong(r.date)}
                          </div>
                        </div>
                        <span className={`text-sm font-medium whitespace-nowrap ${credit ? 'text-emerald-600' : 'text-gray-900'}`} data-id="element-2848">
                          {credit ? '+' : '−'}
                          {formatAmt(r.amount)}
                        </span>
                      </div>
                    </li>;
            })}
              </ol>
            </div>}

          {/* Reconciliation */}
          <div className="bg-white border border-gray-200 rounded-lg" data-id="element-2849">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-2850">
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-2851">
                Reconciliation
              </h2>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-2852">
                Use these references to match this entry against your own
                systems and bank statements.
              </p>
            </div>
            <div className="px-6 py-2" data-id="element-2853">
              <CopyableId label="Order reference" value={entry.orderRef} data-id="element-2854" />
              {payoutId && <CopyableId label="Payout ID" value={payoutId} data-id="element-2855" />}
              {payoutEntry && <SummaryRow label="Payout arrival" value={formatDateTimeLong(payoutEntry.date)} data-id="element-2856" />}
            </div>
          </div>
        </div>

        {/* Right: IDs panel */}
        <aside className="space-y-6" data-id="element-2857">
          <div className="bg-white border border-gray-200 rounded-lg" data-id="element-2858">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-2859">
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-2860">
                Identifiers
              </h2>
            </div>
            <div className="px-6 py-2" data-id="element-2861">
              <CopyableId label="Balance ledger ID" value={entry.id} data-id="element-2862" />
              <CopyableId label="Source reference" value={entry.reference} data-id="element-2863" />
              {chargeId && <CopyableId label="Charge ID" value={chargeId} data-id="element-2864" />}
              {payoutId && <CopyableId label="Payout ID" value={payoutId} data-id="element-2865" />}
              {feeId && <CopyableId label="Fee ID" value={feeId} data-id="element-2866" />}
              <CopyableId label="Order reference" value={entry.orderRef} data-id="element-2867" />
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg" data-id="element-2868">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-2869">
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-2870">Context</h2>
            </div>
            <div className="px-6 py-2" data-id="element-2871">
              <CopyableId label="Customer" value={entry.customer && entry.customer !== '—' ? entry.customer : null} mono={false} data-id="element-2872" />
              <CopyableId label="Sub-merchant" value={entry.companyId} mono={false} data-id="element-2873" />
            </div>
          </div>

          {payoutEntry && payoutEntry.id !== entry.id && <Link to={`/balance-activity/${payoutEntry.id}`} className="flex items-center justify-between gap-2 px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors" data-id="element-2874">
              <span data-id="element-2875">View linked payout</span>
              <ExternalLinkIcon size={14} className="text-gray-400" data-id="element-2876" />
            </Link>}
        </aside>
      </div>
    </div>;
}
function SummaryRow({
  label,
  value,
  valueClass
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return <div className="flex items-center justify-between gap-4 px-6 py-3" data-id="element-2877">
      <dt className="text-sm text-gray-500" data-id="element-2878">{label}</dt>
      <dd className={`text-sm text-gray-900 ${valueClass ?? ''}`} data-id="element-2879">{value}</dd>
    </div>;
}